// `$placeholder` substitution for cell templates.
//
// Carries v6's `applyPlaceholders` pattern (see v6
// `src/app/api/admin/scaffold-notebook/route.ts`):
//   1. Build a map of `$key → value`
//   2. Sort keys longest-first so `$contractAddress` doesn't get mangled
//      by a naive `$contract` pass
//   3. Replace via split+join (no regex; keys are alphanumeric)
//
// In v6 substitution happened server-side at scaffold time; v7 §5.6.3 runs
// client-side in the cell host so the same template files (`content/...`)
// can be reused without a build step. Phase 6 swaps the static substitution
// for the live agent which produces fully-resolved markdown — same call
// site, different source.

import { parse as parseYaml } from 'yaml';
import type { EnrichedSubscription } from './investor';
import { OFFERINGS } from './cells';
import { api } from '@rmint/shell';

// Per-restaurant facts. §6.7 — loaded from /dossier/restaurants/{rmintId}/profile.yaml.
export type RestaurantFacts = {
  contractAddress: string;
  saleAddress: string;
  tokenAddress: string;
  restaurantName: string;
  restaurantId: string;
  chef: string;
  cuisine: string;
  location: string;
  city: string;
  country: string;
  seatCount: string;
  chefBio: string;
  revenueSplitPercent: string;
  spvEntityName: string;
  jurisdiction: string;
  website: string;
  instagram: string;
  // Offering economics — mirror of on-chain TokenSale params (Slice D).
  // pricePerToken-Display, etc. are pre-formatted USD strings; *Raw
  // are USDC-6dec integers as strings (preserved for forward-compat).
  pricePerToken: string;
  minPurchase: string;
  maxPurchase: string;
  softCap: string;
  hardCap: string;
  lockupDisplay: string;
  distributionCadence: string;
};

// ─── Dossier fetch ───────────────────────────────────────────────

// Per-session cache: one Promise<RestaurantFacts | null> per rmintId/slug.
// Second callers get the in-flight promise, not a duplicate fetch.
const _factsCache = new Map<string, Promise<RestaurantFacts | null>>();

// Adapt the raw YAML parse result (nested objects) into the flat
// RestaurantFacts shape consumed by buildBindings. Missing fields
// silently become empty string so downstream callers stay safe.
export function adaptToRestaurantFacts(raw: Record<string, unknown>): RestaurantFacts {
  const contracts = (raw.contracts ?? {}) as Record<string, unknown>;
  const chef = (raw.chef ?? {}) as Record<string, unknown>;
  const spv = (raw.spv ?? {}) as Record<string, unknown>;
  const links = (raw.links ?? {}) as Record<string, unknown>;
  const offering = (raw.offering ?? {}) as Record<string, unknown>;

  function str(v: unknown): string {
    if (typeof v === 'number') return String(v);
    return typeof v === 'string' ? v.trim() : '';
  }

  return {
    restaurantId: str(raw.rmintId),
    restaurantName: str(raw.name),
    contractAddress: str(contracts.contractAddress),
    saleAddress: str(contracts.saleAddress),
    tokenAddress: str(contracts.tokenAddress),
    chef: str(chef.name),
    chefBio: str(chef.bio),
    cuisine: str(raw.cuisine),
    location: str(raw.location),
    city: str(raw.city),
    country: str(raw.country),
    seatCount: str(raw.seatCount),
    revenueSplitPercent: str(raw.revenueSplitPercent),
    spvEntityName: str(spv.entityName),
    jurisdiction: str(spv.jurisdiction),
    website: str(links.website),
    instagram: str(links.instagram),
    pricePerToken: str(offering.pricePerTokenDisplay),
    minPurchase: str(offering.minPurchaseDisplay),
    maxPurchase: str(offering.maxPurchaseDisplay),
    softCap: str(offering.softCapDisplay),
    hardCap: str(offering.hardCapDisplay),
    lockupDisplay: str(offering.lockupDisplay),
    distributionCadence: str(offering.distributionCadence),
  };
}

export function fetchRestaurantFacts(rmintId: string): Promise<RestaurantFacts | null> {
  const cached = _factsCache.get(rmintId);
  if (cached) return cached;

  const promise = (async (): Promise<RestaurantFacts | null> => {
    try {
      const res = await api.get<string>(`/dossier/restaurants/${rmintId}/profile.yaml`);
      if (res.status !== 200 || typeof res.data !== 'string') return null;
      const parsed = parseYaml(res.data) as Record<string, unknown>;
      if (typeof parsed !== 'object' || parsed === null) return null;
      return adaptToRestaurantFacts(parsed);
    } catch {
      return null;
    }
  })();

  _factsCache.set(rmintId, promise);
  return promise;
}

// ─── Binding builder ─────────────────────────────────────────────

export type BindingContext = {
  contractId?: string;
  investment?: EnrichedSubscription | null;
  walletAddress?: string | undefined;
  investorId?: string | undefined;
  // §6.7 option (b): caller fetches facts via fetchRestaurantFacts
  // and passes them in, keeping buildBindings synchronous.
  facts?: RestaurantFacts | null;
};

export type Bindings = Record<string, string>;

// Build a flat `$key → value` map from the active context. Unbound
// placeholders pass through unchanged so `$contract.sale` syntax (a
// runtime binding the agent resolves at exec-time in v6) survives
// the substitution; Phase 5 displays them verbatim, Phase 6's agent
// will resolve the deeper bindings.
export function buildBindings(ctx: BindingContext): Bindings {
  const facts = ctx.facts ?? null;
  const offering = ctx.contractId ? OFFERINGS.find((o) => o.id === ctx.contractId) : null;

  const out: Bindings = {};

  if (facts) {
    out.$contractAddress = facts.contractAddress;
    out.$tokenAddress = facts.tokenAddress;
    out.$saleAddress = facts.saleAddress;
    out.$restaurantName = facts.restaurantName;
    out.$restaurantId = facts.restaurantId;
    out.$chef = facts.chef;
    out.$chefBio = facts.chefBio;
    out.$cuisine = facts.cuisine;
    out.$location = facts.location;
    out.$city = facts.city;
    out.$country = facts.country;
    out.$seatCount = facts.seatCount;
    out.$revenueSplitPercent = facts.revenueSplitPercent;
    out.$spvEntityName = facts.spvEntityName;
    out.$jurisdiction = facts.jurisdiction;
    out.$website = facts.website;
    out.$instagram = facts.instagram;
    // Offering economics (Slice D — mirror of on-chain TokenSale).
    out.$pricePerToken = facts.pricePerToken;
    out.$minPurchase = facts.minPurchase;
    out.$maxPurchase = facts.maxPurchase;
    out.$softCap = facts.softCap;
    out.$hardCap = facts.hardCap;
    out.$lockupDisplay = facts.lockupDisplay;
    out.$distributionCadence = facts.distributionCadence;
  } else if (offering) {
    // Offering recognized but no facts table (e.g., a future offering
    // before facts are filled in). Still bind what we can.
    out.$restaurantName = offering.label;
    out.$contractAddress = offering.contractAddress;
  }

  if (ctx.investment) {
    out.$subscriptionId = ctx.investment.subscriptionId;
    out['$subscription.id'] = ctx.investment.subscriptionId;
    const phase = ctx.investment.state?.currentPhase ?? '';
    if (phase) out['$subscription.phase'] = phase;
    const lastEvent = ctx.investment.state?.lastEvent?.eventType;
    if (lastEvent) out['$subscription.lastEvent'] = lastEvent;
    // The amount field isn't on state.current — v6 read it from the
    // subscription's intent.shown body. Phase 5 leaves $subscription.amountUsd
    // unbound so it surfaces as plain text "$subscription.amountUsd".
    const subjectInvestor = ctx.investment.subject?.investorId;
    const subjectRmint = ctx.investment.subject?.rmintId;
    if (subjectInvestor) out['$subject.investorId'] = subjectInvestor;
    if (subjectRmint) out['$subject.rmintId'] = subjectRmint;
  }

  if (ctx.walletAddress) {
    out.$wallet = ctx.walletAddress;
    out.$walletAddress = ctx.walletAddress;
  }

  if (ctx.investorId) {
    out.$investorId = ctx.investorId;
  }

  return out;
}

// ─── Substitution ────────────────────────────────────────────────

// Replace every $placeholder in `source` with its bound value. Keys
// are processed longest-first so `$contractAddress` is replaced before
// `$contract`; v6 RFC §applyPlaceholders for rationale.
export function applyBindings(source: string, bindings: Bindings): string {
  const keys = Object.keys(bindings).sort((a, b) => b.length - a.length);
  let out = source;
  for (const key of keys) {
    out = out.split(key).join(bindings[key]!);
  }
  return out;
}
